"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import { 
  toggleRatingFilter, 
  toggleTopicFilter, 
  clearAllFilters, 
  setAppliedFilters,
  setShowFilterSidebar 
} from "@/app/lib/features/productReview/productReview.slice";
import { getProductReviewsList as getProductReviewsListThunk } from "@/app/lib/features/productReview/productReview.thunk";
import { Button } from "@/app/components/shadcn/button";
import StarRating from "@/app/components/elements/StarRating";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const FilterSidebar = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { pendingFilters, currentProductSlug, filterGroupsData, showFilterSidebar } = useProductReview();

  const handleToggle = useCallback((groupKey: string, itemKey: string | number) => {
    if (groupKey.includes('Konu')) {
      dispatch(toggleTopicFilter(itemKey.toString()));
    } else if (groupKey.includes('Puan')) {
      const numKey = typeof itemKey === 'number' ? itemKey : parseInt(itemKey.toString());
      dispatch(toggleRatingFilter(numKey));
    }
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    if (!currentProductSlug) return;

    trackGeneric("review_filter_clear", "click");
    dispatch(clearAllFilters());
    dispatch(setAppliedFilters({}));
    
    // Use router.replace with scroll: false to prevent scrolling to top
    router.replace(window.location.pathname, { scroll: false });

    dispatch(
      getProductReviewsListThunk({
        productSlug: currentProductSlug,
        filters: { page: 1, per_page: 15 },
      }),
    );
  }, [dispatch, router, currentProductSlug]);

  const handleApply = useCallback(() => {
    if (!currentProductSlug) return;

    trackGeneric("review_filter_apply", "click");
    const params = new URLSearchParams();

    if (pendingFilters.rating && pendingFilters.rating.length > 0) {
      pendingFilters.rating.forEach((r) => params.append("rating", r.toString()));
    }

    if (pendingFilters.topic && pendingFilters.topic.length > 0) {
      pendingFilters.topic.forEach((t) => params.append("topic", t));
    }

    if (pendingFilters.seller_id) {
      params.set("seller_id", pendingFilters.seller_id.toString());
    }

    if (pendingFilters.search) {
      params.set("search", pendingFilters.search);
    }

    if (pendingFilters.sort) {
      params.set("sort", pendingFilters.sort);
    }

    const queryString = params.toString();
    const newUrl = queryString ? `?${queryString}` : window.location.pathname;
    
    // Use router.replace with scroll: false to prevent scrolling to top
    router.replace(newUrl, { scroll: false });

    dispatch(setAppliedFilters(pendingFilters));

    dispatch(
      getProductReviewsListThunk({
        productSlug: currentProductSlug,
        filters: { ...pendingFilters, page: 1, per_page: 15 },
      }),
    );

    dispatch(setShowFilterSidebar(false));
  }, [pendingFilters, router, dispatch, currentProductSlug]);

  const handleClose = useCallback(() => {
    dispatch(setShowFilterSidebar(false));
  }, [dispatch]);

  if (!showFilterSidebar) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-998" onClick={handleClose} />
      <aside className="h-screen w-88 fixed top-0 left-0 bg-white z-999 shadow-2xl flex flex-col">
        <div className="p-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Tüm Filtreler</h3>
            <Button variant="ghost" size="icon-sm" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-6">
            {filterGroupsData?.groups.map((group, groupIndex) => (
              <div key={groupIndex}>
                <h4 className="font-medium mb-3">{group.title}</h4>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((item) => {
                    const itemKey = item.key;
                    const groupKey = group.title;
                    
                    let isSelected = false;
                    if (groupKey.includes('Konu')) {
                      isSelected = (pendingFilters?.topic || []).includes(itemKey.toString());
                    } else if (groupKey.includes('Puan')) {
                      const numKey = typeof itemKey === 'number' ? itemKey : parseInt(itemKey.toString());
                      isSelected = (pendingFilters?.rating || []).includes(numKey);
                    }
                    
                    return (
                      <Button
                        key={itemKey}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleToggle(groupKey, itemKey)}
                        className="text-xs h-8 flex items-center gap-1"
                      >
                        {/* Sayaç 0 ise parantez hiç basılmaz. */}
                        {item.rating !== undefined ? (
                          <>
                            <StarRating rating={item.rating} size="xs" className="gap-0.5" />
                            {item.count > 0 && `(${item.count})`}
                          </>
                        ) : item.count > 0 ? (
                          `${item.text} (${item.count})`
                        ) : (
                          item.text
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 shrink-0">
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleClearAll}>
              Temizle
            </Button>
            <Button className="flex-1" onClick={handleApply}>
              Uygula
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default FilterSidebar;